import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { VStack } from "@/components/ui/v-stack";
import { ScrollingWizardContainer } from "@/components/load-creation/scrolling-wizard-container";
import { LoadFormProvider, useLoadForm } from "@/lib/contexts/LoadFormContext";
import { Step1RouteSelection } from "@/components/load-creation/steps/step-1-route-selection";
import { Step2LoadDetails } from "@/components/load-creation/steps/step-2-load-details";
import { Step3AgreementTerms } from "@/components/load-creation/steps/step-3-agreement-terms";
import { Step3Payment } from "@/components/load-creation/steps/step-3-payment";
import { Step4Review } from "@/components/load-creation/steps/step-4-review";
import { Step5Documents } from "@/components/load-creation/steps/step-5-documents";
import { getLoad, resubmitLoad, type Load } from "@/lib/api/loads";

function LoadEditContent({
  loadId,
  loadDisplayId,
  isDeclined,
}: {
  loadId: string;
  loadDisplayId: string;
  isDeclined: boolean;
}) {
  const navigate = useNavigate();
  const { t: tLoads } = useTranslation("loads");
  const { t: tTickets } = useTranslation("tickets");
  const { handleSubmit } = useLoadForm();

  const handleCancel = () => {
    void navigate({ to: "/loads/$id", params: { id: loadId } });
  };

  const handleSave = async () => {
    const result = await handleSubmit();
    if (result) {
      void navigate({ to: "/loads/$id", params: { id: loadId } });
    }
  };

  const handleResubmit = async () => {
    const result = await handleSubmit();
    if (!result) return;
    try {
      await resubmitLoad(loadId);
      void navigate({ to: "/loads/$id", params: { id: loadId } });
    } catch (err) {
      console.error("Failed to resubmit load:", err);
    }
  };

  const sections = useMemo(
    () => [
      {
        id: "route",
        title: tLoads("route"),
        component: <Step1RouteSelection />,
      },
      {
        id: "cargo",
        title: tLoads("cargo"),
        component: <Step2LoadDetails />,
      },
      {
        id: "payment",
        title: tLoads("financials"),
        component: <Step3Payment />,
      },
      {
        id: "terms",
        title: tLoads("agreementTermsTitle"),
        component: <Step3AgreementTerms />,
      },
      {
        id: "documents",
        title: tLoads("documents"),
        component: <Step5Documents />,
      },
      {
        id: "review",
        title: tLoads("review"),
        component: <Step4Review />,
      },
    ],
    [tLoads],
  );

  return (
    <>
      {/* load-edit-page */}
      <ScrollingWizardContainer
        sections={sections}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={tLoads("saveChanges")}
        editMode
        editTitle={loadDisplayId}
        onBack={() => void navigate({ to: "/loads" })}
        onResubmit={isDeclined ? handleResubmit : undefined}
        resubmitLabel={
          isDeclined ? tTickets("entitySection.resubmitForApproval") : undefined
        }
      />
    </>
  );
}

function EditLoadPage() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLoad(params.id)
      .then((data) => {
        if (!data.is_editable) {
          void navigate({
            to: "/loads/$id",
            params: { id: params.id },
            replace: true,
          });
          return;
        }
        setLoad(data);
      })
      .catch(() => {
        void navigate({
          to: "/loads/$id",
          params: { id: params.id },
          replace: true,
        });
      })
      .finally(() => setLoading(false));
  }, [params.id, navigate]);

  if (loading || !load) {
    return (
      <VStack gap="lg" className="max-w-5xl mx-auto">
        <Skeleton className="h-40 w-full" rounded="lg" />
        <Skeleton className="h-16 w-full" rounded="lg" />
        <Skeleton className="h-16 w-full" rounded="lg" />
        <Skeleton className="h-16 w-full" rounded="lg" />
        <Skeleton className="h-16 w-full" rounded="lg" />
      </VStack>
    );
  }

  return (
    <LoadFormProvider editLoadId={params.id} initialLoad={load}>
      <LoadEditContent
        loadId={params.id}
        loadDisplayId={load.load_id}
        isDeclined={load.status === "Declined"}
      />
    </LoadFormProvider>
  );
}

export const Route = createFileRoute("/_app/loads/$id/edit")({
  component: EditLoadPage,
});
